import { storageService } from 'src/services/app/storage.service'
import { smartMatch } from 'src/services/util.service'
import { STORAGE_KEYS } from 'src/services/consts/api.const'
import {
  ACTIVE_STATUSES,
  DPF_FOUND_STATUSES,
  AI_SERVICES_STATUSES,
  getDefaultFilter,
  getDefaultTableState,
} from 'src/services/consts/companies.const'

// Filter and search functions
export function filterCompanies(companies, filterBy) {
  let filteredCompanies = companies

  // Text search
  if (filterBy.txt?.trim()) {
    const searchTerm = filterBy.txt.trim()
    filteredCompanies = smartMatch(filteredCompanies, searchTerm)
  }

  filteredCompanies = filteredCompanies.filter((company) => {
    // Country filter
    if (filterBy.country) {
      const countryMatch = company.country === filterBy.country
      if (!countryMatch) return false
    }

    // Date range filter
    if (filterBy.dateAddedStart || filterBy.dateAddedEnd) {
      const companyDate = new Date(company.dateAdded)
      companyDate.setHours(0, 0, 0, 0) // Normalize company date to start of day

      if (filterBy.dateAddedStart) {
        const startDate = new Date(filterBy.dateAddedStart)
        startDate.setHours(0, 0, 0, 0) // Normalize start date to start of day
        if (companyDate < startDate) return false
      }
      if (filterBy.dateAddedEnd) {
        const endDate = new Date(filterBy.dateAddedEnd)
        endDate.setHours(23, 59, 59, 999) // Set end date to end of day
        if (companyDate > endDate) return false
      }
    }

    // Active status filter
    if (filterBy.active !== ACTIVE_STATUSES.ALL) {
      const companyIsActive = company.active
      if (filterBy.active === ACTIVE_STATUSES.ACTIVE && !companyIsActive) {
        return false
      }
      if (filterBy.active === ACTIVE_STATUSES.ARCHIVED && companyIsActive) {
        return false
      }
    }

    // DPF Found filter
    if (filterBy.dpfFound !== DPF_FOUND_STATUSES.ALL) {
      const companyDpfFound = company.isDpfFound
      if (filterBy.dpfFound === DPF_FOUND_STATUSES.FOUND && !companyDpfFound) {
        return false
      }
      if (filterBy.dpfFound === DPF_FOUND_STATUSES.NOT_FOUND && companyDpfFound) {
        return false
      }
    }

    // AI Services filter
    if (filterBy.aiServices !== AI_SERVICES_STATUSES.ALL) {
      const companyProvidesAi = company.providesAiServices
      if (filterBy.aiServices === AI_SERVICES_STATUSES.PROVIDES && !companyProvidesAi) {
        return false
      }
      if (filterBy.aiServices === AI_SERVICES_STATUSES.DOES_NOT_PROVIDE && companyProvidesAi) {
        return false
      }
    }

    return true
  })

  return filteredCompanies
}

// Query string parsing
export function parseIntoQuery(params) {
  const queryParams = {}

  const defaultData = { ...getDefaultFilter(), ...getDefaultTableState() }

  Object.entries(params).forEach(([key, value]) => {
    // Handle different types of empty values
    if (value === '' || value === undefined) return
    // Include non-empty values that are different from defaults
    if (value !== defaultData[key]) {
      // Convert booleans to string for URL
      if (typeof value === 'boolean') {
        queryParams[key] = value.toString()
      } else {
        queryParams[key] = value
      }
    }
  })

  return queryParams
}

// Initial state from URL/cache
export function getInitialFilter(queryParams, ignoreCache = false) {
  const filterBy = getDefaultFilter()
  const cacheParams = storageService.getSession(STORAGE_KEYS.LAST_FILTER_KEY) || {}

  const hasQuery = Object.keys(queryParams).length > 0
  Object.keys(filterBy).forEach((key) => {
    // Use URL params if they exist, otherwise use cache for initial load (unless ignoreCache is true)
    const value = hasQuery ? queryParams[key] : ignoreCache ? undefined : cacheParams[key]
    if (value) {
      filterBy[key] = value
    }
  })
  return filterBy
}

export function getInitialTableState(queryParams, ignoreCache = false) {
  const tableState = getDefaultTableState()
  const cacheParams = storageService.getSession(STORAGE_KEYS.LAST_TABLE_STATE_KEY) || {}

  const hasQuery = Object.keys(queryParams).length > 0
  Object.keys(tableState).forEach((key) => {
    // Use URL params if they exist, otherwise use cache for initial load (unless ignoreCache is true)
    const value = hasQuery ? queryParams[key] : ignoreCache ? undefined : cacheParams[key]
    if (value) {
      if (key === 'descending') {
        // descending remains boolean
        const isBoolean = typeof value === 'boolean'
        tableState[key] = isBoolean ? value : value === 'true'
      } else if (['page', 'rowsPerPage'].includes(key)) {
        tableState[key] = parseInt(value, 10)
      } else {
        tableState[key] = value
      }
    }
  })
  return tableState
}

// Data transformation functions
export function refactorCompany(company) {
  return {
    id: company.company_id,
    active: company.active,
    name: company.company_name,
    legalName: company.company_legal_name,
    country: company.country,
    dateAdded: company.date_added,
    isDpfFound: company.dpf_found,
    parentId: company.parent_id,
    providesAiServices: company.provides_ai_services,
    website: company.website,
  }
}

export function refactorCompanies(companies) {
  if (!companies) return []
  return companies.map((company) => refactorCompany(company))
}
